import React, { useState, createContext, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { auth, db } from "../firebase";
import {
  hasShopUserClaims,
  resolveShopUserToken,
  SHOP_NOT_SHOP_USER,
} from "./shopAuth";

const defaultFirebaseContext = {
  user: null,
};

export const AuthContext = createContext(defaultFirebaseContext);

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [region, setRegion] = useState(null)

  const [isInitialized, setIsInitialized] = useState(false);

  const collectionPrefix = useMemo(
    () => (user ? `/regions/${user.claims.regionId}` : null),
    [user]
  );
  const jobsRef = useMemo(
    () => (collectionPrefix ? db.collection(`${collectionPrefix}/jobs`) : null),
    [collectionPrefix]
  );
  const techsRef = useMemo(
    () =>
      collectionPrefix ? db.collection(`${collectionPrefix}/techs`) : null,
    [collectionPrefix]
  );
  const shopsRef = useMemo(
    () =>
      collectionPrefix ? db.collection(`${collectionPrefix}/shops`) : null,
    [collectionPrefix]
  );

  useEffect(() => {
    if (user?.claims?.regionId) {
      return db.doc(`/regions/${user.claims.regionId}`).onSnapshot(regionSnapshot => {
        setRegion(
          regionSnapshot.exists ? { ...regionSnapshot.data(), id: regionSnapshot.id } : null
        )
      });
    }
    setRegion(null)
  }, [user?.claims?.regionId]);

  useEffect(() => {
    return auth.onAuthStateChanged(async (u) => {
      if (u) {
        try {
          const { token } = await resolveShopUserToken(u);
          if (!hasShopUserClaims(token?.claims)) {
            setError(SHOP_NOT_SHOP_USER);
            await auth.signOut();
            return;
          }

          setError(null);

          setUser({
            ...u,
            claims: token.claims,
          });
          setIsInitialized(true);
        } catch {
          setError(SHOP_NOT_SHOP_USER);
          await auth.signOut();
        }
      } else {
        setUser(null);
        setIsInitialized(true);
      }
    });
  }, [setUser, setError]);

  return (
    <AuthContext.Provider
      value={{
        user,
        collectionPrefix,
        jobsRef,
        techsRef,
        shopsRef,
        region,
        initialized: isInitialized,
        error,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

UserProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
